import React from 'react';
import type { RouteProp } from '@react-navigation/native';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import type {
  AddressType,
  GooglePlacesAddress,
  PinData,
  Region,
} from '@sweetgreen/garnish';
import {
  AddressTypes,
  formatGoogleAddress,
  useEffectOnce,
} from '@sweetgreen/garnish';

import type { AddAddressSuccess, Address, Customer } from '@order/graphql';
import { DeliveryPreferenceType } from '@order/graphql';
import type {
  FormatXMLElementFn,
  PrimitiveType,
  TranslationKeys,
} from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';
import { useOrders } from '@order/Order';

import { useAddAddressMutation } from '../../../components/Address/GraphQL/Address.generated';
import type { MenuTabStackParamList } from '../../../navigation/AppNavigation.props';
import type {
  LocationPredictionsData,
  LocationQueryType,
} from '../LocationScreen.types';
import { DEFAULT_REGION } from '../LocationScreen.utils';
import { useNavigateToMenuFromLocation } from './LocationScreen.navigation';
import { useLocationsByAddress } from './LocationScreen.search';

export const useDelivery = ({
  customer,
  isFetchingCustomer,
  searchString,
  locationSearchType,
  setCustomerData,
  setLocationSearchType,
  setLocationQueryType,
  setSearchString,
  setRegion,
}: {
  readonly customer: Partial<Customer>;
  readonly isFetchingCustomer: boolean;
  readonly searchString: string;
  readonly locationSearchType: AddressType;
  readonly setLocationSearchType: (type: AddressType) => void;
  readonly setLocationQueryType: (type: LocationQueryType) => void;
  readonly setSearchString: (query: string) => void;
  readonly setRegion: (region: Region) => void;
  readonly setCustomerData: (customer: Partial<Customer>) => void;
}) => {
  // ───────────────────────── Order History ────────────────────────────────
  const { orders, isLoadingOrders, fetchOrders } = useOrders();
  const isLoadingDeliveryLocation = isFetchingCustomer || isLoadingOrders;
  const isDelivery = locationSearchType === 'delivery';

  useFocusEffect(
    React.useCallback(() => {
      fetchOrders();
    }, [fetchOrders]),
  );

  // ────────────────────── Predictions ─────────────────────────────────────
  const [placeId, setPlaceId] = React.useState('');
  const [predictionsSearchString, setPredictionsSearchString] =
    React.useState(searchString);
  const [predictionsData, setPredictions] = React.useState(defaultPredictions);

  const setPredictionsData = React.useCallback(
    (data: LocationPredictionsData) => {
      const current = JSON.stringify(predictionsData);
      const next = JSON.stringify(data);
      const hasChanged = current !== next;

      if (hasChanged) setPredictions(data);
    },
    [predictionsData],
  );

  // ────────────────────── Location By Address ─────────────────────────────
  const {
    fetchingDelivery,
    foundLocation,
    noLocationFound,
    googlePlacesAddress,
    setGooglePlacesAddress,
    setLoadingAddress,
  } = useLocationsByAddress(placeId);

  // ───────────────────────────── Navigation ───────────────────────────────
  const navigateToMenu = useNavigateToMenuFromLocation();
  const [addAddressResponse, addAddress] = useAddAddressMutation();
  const isAddingAddress = addAddressResponse?.fetching ?? false;

  React.useEffect(() => {
    const success = addAddressResponse.data?.addAddress as AddAddressSuccess;
    const addressId = success?.address?.id ?? '';

    if (!addressId) return;
    const addresses = customer?.addresses ?? [];
    const hasAddress = addresses.find(({ id }) => id === addressId);

    if (!hasAddress) {
      setCustomerData({
        ...customer,
        addresses: [...(customer?.addresses ?? []), success?.address],
      });
    }

    navigateToMenu({
      orderType: 'delivery',
      restaurantId: foundLocation?.restaurant?.id,
      restaurantSlug: foundLocation?.restaurant?.slug,
      restaurantName: foundLocation?.restaurant?.name,
      deliveryAddressName: success?.address?.name ?? '',
      deliveryOrderDetail: {
        addressId: success?.address?.id,
        tip: 0,
        deliveryFee: foundLocation?.restaurant?.deliveryFee ?? 0,
        vendor: foundLocation?.vendor?.name ?? '',
        vendorRestaurantId: foundLocation?.vendor?.restaurantId ?? '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAddressResponse]);

  // ───────────────────── Change Delivery Region & Pin ──────────────────────
  const [deliveryPin, setDeliveryPin] = React.useState<PinData | null>(null);
  const changeDeliveryRegion = React.useCallback(
    (address: Partial<GooglePlacesAddress>) => {
      const addressName =
        (address as { readonly name: string })?.name ?? address?.street;
      const formattedAddress = formatGoogleAddress(address);
      const addresses = customer?.addresses ?? [];
      const customerAddress = addresses.find(
        ({ googlePlaceId }) => googlePlaceId === address?.googlePlaceId,
      );
      const isHomeOrWork = ['home', 'work'].includes(
        customerAddress?.name ?? '',
      );
      const customOrUndefined = customerAddress?.name ? 'custom' : undefined;
      const deliveryAddressType = (
        isHomeOrWork ? customerAddress?.name ?? 'home' : customOrUndefined
      ) as 'home' | 'work' | 'custom' | undefined;

      setSearchString(formattedAddress ?? '');
      setPlaceId(address.googlePlaceId ?? '');
      setRegion({
        ...DEFAULT_REGION,
        lat: address.latitude ?? 0,
        lng: address.longitude ?? 0,
      });
      setDeliveryPin((prev) => ({
        id: address.googlePlaceId ?? '',
        restaurantSlug: '',
        name:
          prev?.id === address.googlePlaceId
            ? prev?.name ?? addressName
            : addressName,
        address: formattedAddress,
        lat: address.latitude ?? 0,
        lng: address.longitude ?? 0,
        active: true,
        acceptingOrders: true,
        isDeliveryAddress: true,
        deliveryAddressType,
      }));
    },
    [customer?.addresses, setRegion, setSearchString],
  );

  // ───────────────────── Load Delivery Location ──────────────────────
  const { t } = useLocalizationContext();
  const deliveryOrder = orders.find(
    (order) => order.orderType === AddressTypes.delivery.toUpperCase(),
  );

  // Previous Address
  const { params } = useRoute<RouteProp<MenuTabStackParamList, 'Location'>>();
  const previousDeliveryId = params?.previousDeliveryId;
  const previousAddress = customer?.addresses?.find(
    ({ id }) => id === previousDeliveryId,
  );

  // Load delivery channel info
  // 1. Previous address (from last menu).
  // 2. Last delivery order address.
  // 3. First customer address.
  const { address } = getDeliveryChannelInfo({
    t,
    address: previousAddress ?? customer?.addresses?.[0],
    order: deliveryOrder,
  });

  useEffectOnce(() => {
    if (isLoadingDeliveryLocation) return false;

    if (!isDelivery || !address) return false;

    changeDeliveryRegion(address);

    return true;
  }, [
    address,
    isDelivery,
    isFetchingCustomer,
    isLoadingOrders,
    isLoadingDeliveryLocation,
    setPlaceId,
    changeDeliveryRegion,
  ]);

  // ───────────────────── Sync Delivery Prediction ──────────────────────
  React.useEffect(() => {
    if (!googlePlacesAddress?.latitude || !isDelivery) return;

    changeDeliveryRegion(googlePlacesAddress);
  }, [googlePlacesAddress, isDelivery, changeDeliveryRegion]);

  // synchronize search strings in order to maintain consistent results for each search type
  React.useEffect(() => {
    setPredictionsSearchString(searchString);
  }, [searchString, setPredictionsSearchString]);

  // ─────────── going pickup/outpost from delivery runs query ───────────
  React.useEffect(() => {
    if (locationSearchType === 'delivery') return;

    if (!googlePlacesAddress.formattedAddress) return;

    setLocationQueryType('searchString');
    setSearchString(formatGoogleAddress(googlePlacesAddress) ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearchType]);

  // ─────────────────────────── Callbacks ───────────────────────────────
  const onOrderNow = React.useCallback(() => {
    const addresses = customer?.addresses ?? [];
    const existingAddress = addresses.find(
      ({ googlePlaceId }) =>
        googlePlaceId === googlePlacesAddress.googlePlaceId,
    );

    if (existingAddress?.id) {
      navigateToMenu({
        orderType: 'delivery',
        restaurantId: foundLocation?.restaurant?.id,
        restaurantSlug: foundLocation?.restaurant?.slug,
        restaurantName: foundLocation?.restaurant?.name,
        deliveryAddressName: existingAddress?.name ?? '',
        deliveryOrderDetail: {
          addressId: existingAddress?.id,
          tip: 0,
          deliveryFee: foundLocation?.restaurant?.deliveryFee ?? 0,
          vendor: foundLocation?.vendor?.name ?? '',
          vendorRestaurantId: foundLocation?.vendor?.restaurantId ?? '',
        },
      });

      return;
    }

    void addAddress({
      input: {
        googlePlaceId: googlePlacesAddress.googlePlaceId ?? '',
        zipCode: googlePlacesAddress.zipCode ?? '',
        street: googlePlacesAddress.street ?? '',
        city: googlePlacesAddress.city ?? '',
        state: googlePlacesAddress.state ?? '',
        country: googlePlacesAddress.country ?? '',
        latitude: googlePlacesAddress.latitude ?? 0,
        longitude: googlePlacesAddress.longitude ?? 0,
        deliveryPreference: DeliveryPreferenceType.LeaveAtDoor,
        name: '',
        secondaryStreet: '',
        notes: '',
      },
    });
  }, [
    customer?.addresses,
    addAddress,
    googlePlacesAddress.googlePlaceId,
    googlePlacesAddress.zipCode,
    googlePlacesAddress.street,
    googlePlacesAddress.city,
    googlePlacesAddress.state,
    googlePlacesAddress.country,
    googlePlacesAddress.latitude,
    googlePlacesAddress.longitude,
    navigateToMenu,
    foundLocation?.restaurant?.id,
    foundLocation?.restaurant?.slug,
    foundLocation?.restaurant?.name,
    foundLocation?.restaurant?.deliveryFee,
    foundLocation?.vendor?.name,
    foundLocation?.vendor?.restaurantId,
  ]);
  const onResetDeliverySearch = React.useCallback(
    (clearSearch?: boolean) => {
      setPlaceId('');
      setDeliveryPin(null);
      setGooglePlacesAddress({});
      setLoadingAddress(false);
      setPredictionsData(defaultPredictions);

      if (clearSearch) setSearchString('');
    },
    [
      setGooglePlacesAddress,
      setLoadingAddress,
      setPredictionsData,
      setSearchString,
    ],
  );
  const onOrderPickup = React.useCallback(() => {
    setLocationSearchType('pickup');
  }, [setLocationSearchType]);

  // ─────────────────────────────────────────────────────────────────

  const onPredictionPress = React.useCallback(
    (selectedPlaceId: string) => {
      const hasSelectedActiveAddress =
        googlePlacesAddress?.googlePlaceId === selectedPlaceId;

      // update predictions search string if the user selects a prediction that matches active location
      if (hasSelectedActiveAddress)
        setPredictionsSearchString(formatGoogleAddress(googlePlacesAddress));

      setPlaceId(selectedPlaceId);
    },
    [googlePlacesAddress],
  );

  // ───────────────────────────── Response ─────────────────────────────────
  return {
    deliveryPin,
    isAddingAddress,
    googlePlacesAddress,
    foundLocation,
    fetchingDelivery,
    noLocationFound,
    predictionsData,
    predictionsSearchString,
    changeDeliveryRegion,
    setPredictionsData,
    setPredictionsSearchString,
    onPredictionPress,
    onOrderNow,
    onResetDeliverySearch,
    onOrderPickup,
  };
};

const getDeliveryChannelInfo = ({
  t,
  address,
  order,
}: {
  readonly t: TranslationType;
  readonly address?: Address;
  readonly order?: ReturnType<typeof useOrders>['orders'][number];
}) => {
  if (order?.deliveryOrderDetail) {
    return {
      address: order.deliveryOrderDetail.address,
      deliveryChannelText: t('home.featured-channels.to-messaging', {
        location: order.deliveryOrderDetail.address.name,
      }),
      a11y: t('home.featured-channels.a11y-label', {
        orderType: AddressTypes.delivery,
        location: order.deliveryOrderDetail.address.name,
      }),
    };
  }

  if (address?.name) {
    return {
      deliveryChannelText: t('home.featured-channels.to-messaging', {
        location: address.name,
      }),
      address,
      a11y: t('home.featured-channels.a11y-label', {
        orderType: AddressTypes.delivery,
        location: address.name,
      }),
    };
  }

  return {
    deliveryChannelText: t('home.featured-channels.default-delivery'),
    address: null,
    a11y: t('home.featured-channels.a11y-delivery-label'),
  };
};

type TranslationType = (
  key: TranslationKeys,
  values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>,
) => string;

const defaultPredictions: LocationPredictionsData = {
  predictions: [],
  error: undefined,
  loading: false,
  noResults: false,
};
